import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, MoreHorizontal, Pencil, Mail, Truck, Printer, RotateCcw,
  Trash2, Package, MapPin, CreditCard, ClipboardList, History, Copy,
  ExternalLink, RefreshCw, XCircle,
} from "lucide-react";
import EditOrderDialog from "@/components/EditOrderDialog";
import OrderActivityTimeline from "@/components/OrderActivityTimeline";
import {
  totalVialsForLine, totalVialsForOrder, vialsPerUnit,
  formatLabelForItem, strengthForItem,
} from "@/lib/vialCount";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * AdminOrderWorkspace — Phase 2 Individual Order Workspace.
 *
 * Full-page workspace at /admin/orders/:id. Single-scroll layout, sticky
 * contextual header, structured sections. All actions reuse EXISTING
 * endpoints — this component is a pure presentation + orchestration layer.
 *
 * SCOPE LOCK (per user directive, iter185 Phase 2):
 *   • No backend/schema/email/Shippo/payment/fulfillment logic changes.
 *   • Reuses <EditOrderDialog/> for full order editing (comp/discount/etc).
 *   • Communication panel shows ONLY stored data — never infers email history.
 *   • Activity feed is derived from status_history + edit_history +
 *     label_purchased_at + last_refund_at. Never fabricated.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Tiny presentational helpers ────────────────────────────────────────────
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}
function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24); if (dy < 30) return `${dy}d ago`;
  return d.toLocaleDateString();
}
// Phase Purdy · Pass 1 — restrained apothecary status pill palette.
// Cancelled is muted (no strike-through), pending is warm clay (no dashed border).
function statusTone(s) {
  switch (s) {
    case "paid":       return "bg-mossBg text-moss border-mossBorder";
    case "shipped":    return "bg-charcoal text-bone border-charcoal";
    case "delivered":  return "bg-oxblood text-bone border-oxblood";
    case "cancelled":  return "bg-charcoal/5 text-charcoal/70 border-borderSubtle";
    case "abandoned":  return "bg-charcoal/5 text-charcoal/70 border-borderSubtle";
    case "pending_payment":
    default:           return "bg-clayBg text-clay border-clayBorder";
  }
}
function statusLabel(s) {
  return ({
    pending_payment: "Pending payment",
    paid: "Paid",
    shipped: "Shipped",
    delivered: "Delivered",
    cancelled: "Cancelled",
    abandoned: "Abandoned",
  }[s]) || s || "—";
}
function paymentPill(order) {
  const total = Number(order.total || 0);
  const refunded = Number(order.refunded_amount || 0);
  if (refunded > 0 && refunded >= total - 0.01)
    return { label: "Refunded", tone: "bg-charcoal/5 text-charcoal/70 border-borderSubtle" };
  if (refunded > 0)
    return { label: "Partial refund", tone: "bg-clayBg text-clay border-clayBorder" };
  if (order.status === "cancelled")
    return { label: "Void", tone: "bg-charcoal/5 text-charcoal/70 border-borderSubtle" };
  if (order.status === "abandoned")
    return { label: "Abandoned", tone: "bg-charcoal/5 text-charcoal/70 border-borderSubtle" };
  if (order.status === "pending_payment") {
    const isStripe = (order.payment_method || "").toLowerCase() === "stripe";
    return { label: isStripe ? "Awaiting card" : "Pending", tone: "bg-clayBg text-clay border-clayBorder" };
  }
  return { label: "Paid", tone: "bg-mossBg text-moss border-mossBorder" };
}
function shippingMethod(order) {
  if (order.local_delivery) {
    const mode = (order.local_delivery_mode || "delivery").toLowerCase();
    return mode === "pickup" ? "Local Pickup" : "Local Delivery";
  }
  const snap = order.shipping_snapshot;
  if (snap?.service_display) return snap.service_display;
  if (snap?.carrier) return snap.carrier;
  if (order.tracking_carrier) return order.tracking_carrier;
  return "—";
}
function trackingUrl(carrier, number) {
  if (!number) return null;
  const encoded = encodeURIComponent(String(number).trim());
  switch ((carrier || "").toLowerCase()) {
    case "usps":  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`;
    case "ups":   return `https://www.ups.com/track?tracknum=${encoded}`;
    case "fedex": return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`;
    case "dhl":   return `https://www.dhl.com/en/express/tracking.html?AWB=${encoded}`;
    default:      return null;
  }
}

// iter185 Phase 2 · UI polish — translate edit_history.fields_changed slugs
// into operator-friendly groups. We group related fields (items+subtotal+total
// collapse into "Items"; tracking_number+tracking_carrier into "Tracking"; etc.)
// so the activity feed reads like an operator's log, not a schema diff.
const EDIT_FIELD_GROUPS = [
  { key: "items",       label: "Items",            match: (f) => ["items", "subtotal", "total"].includes(f) },
  { key: "shipping",    label: "Shipping",         match: (f) => f === "shipping" },
  { key: "discount",    label: "Discount",         match: (f) => ["discount", "discount_code"].includes(f) },
  { key: "address",     label: "Shipping address", match: (f) => f === "address" },
  { key: "tracking",    label: "Tracking",         match: (f) => ["tracking_number", "tracking_carrier"].includes(f) },
  { key: "buyer_note",  label: "Buyer note",       match: (f) => f === "note_to_buyer" },
  { key: "admin_note",  label: "Internal note",    match: (f) => f === "admin_notes" },
  { key: "customer",    label: "Customer info",    match: (f) => ["customer_email", "customer_name"].includes(f) },
  { key: "payment",     label: "Payment method",   match: (f) => f === "payment_method" },
  { key: "status",      label: "Order status",     match: (f) => ["status", "status_history"].includes(f) },
];
const IGNORE_EDIT_FIELDS = new Set(["edit_history", "updated_at", "user_id"]);

function describeEdit(fields = []) {
  const groupKeys = [];
  const seen = new Set();
  const unknown = [];
  for (const f of fields) {
    if (IGNORE_EDIT_FIELDS.has(f)) continue;
    let matched = false;
    for (const g of EDIT_FIELD_GROUPS) {
      if (g.match(f)) {
        if (!seen.has(g.key)) { seen.add(g.key); groupKeys.push(g.key); }
        matched = true;
        break;
      }
    }
    if (!matched) {
      const label = String(f).replace(/_/g, " ");
      if (!unknown.includes(label)) unknown.push(label);
    }
  }
  const labels = EDIT_FIELD_GROUPS
    .filter((g) => seen.has(g.key))
    .map((g) => g.label);
  return { labels: [...labels, ...unknown], groupKeys };
}

// Join phrases → "A", "A and B", "A, B and C"
function joinPhrases(list) {
  if (!list || list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function AdminOrderWorkspace() {
  const { orderId } = useParams();
  const nav = useNavigate();

  const [order, setOrder] = useState(null);
  const [productsById, setProductsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actionsOpen, setActionsOpen] = useState(false);

  // Reuse the Phase 1 <EditOrderDialog/> verbatim.
  const [editing, setEditing] = useState(false);
  const editDraftRef = useRef(null);

  // Local mini-dialogs — same endpoints as Admin.jsx, self-contained here.
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingDraft, setTrackingDraft] = useState({ carrier: "", number: "" });
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);

  const loadOrder = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Order not found or unavailable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); /* eslint-disable-next-line */ }, [orderId]);

  // Pull the product catalog once so vialCount.js can resolve strengths/formats.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        const map = {};
        for (const p of (data || [])) map[p.id] = p;
        setProductsById(map);
      } catch { /* non-fatal — vialCount degrades to `—` */ }
    })();
  }, []);

  useEffect(() => {
    if (order) document.title = `BBK · Admin · Order #${order.order_number}`;
    return () => { document.title = "Bulk Buy Kits"; };
  }, [order]);

  // Actions dropdown close-on-outside
  const actionsRef = useRef(null);
  useEffect(() => {
    if (!actionsOpen) return;
    const onDoc = (e) => {
      if (!actionsRef.current || !actionsRef.current.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [actionsOpen]);

  // ─── Action handlers ──────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    if (!order || newStatus === order.status) return;
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status: newStatus });
      toast.success(`Order → ${newStatus}`);
      loadOrder();
    } catch (e) {
      toast.error(formatApiError(e, "Status update failed"));
    }
  };

  const openNote = () => { setNoteDraft(order?.note_to_buyer || ""); setNoteOpen(true); };
  const saveNote = async () => {
    try {
      await api.patch(`/admin/orders/${order.id}/note`, { note_to_buyer: noteDraft });
      toast.success(noteDraft.trim() ? "Note saved" : "Note cleared");
      setNoteOpen(false);
      loadOrder();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    }
  };

  const openTracking = () => {
    setTrackingDraft({ carrier: order?.tracking_carrier || "", number: order?.tracking_number || "" });
    setTrackingOpen(true);
  };
  const saveTracking = async () => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/tracking`, {
        tracking_carrier: trackingDraft.carrier,
        tracking_number: trackingDraft.number,
      });
      if (trackingDraft.number.trim()) {
        toast.success(data?.email_sent ? "Tracking saved — buyer emailed" : "Tracking saved");
      } else {
        toast.success("Tracking cleared");
      }
      setTrackingOpen(false);
      loadOrder();
    } catch (e) {
      toast.error(formatApiError(e, "Tracking save failed"));
    }
  };

  const openRefund = () => {
    const remaining = (Number(order.total) - Number(order.refunded_amount || 0)).toFixed(2);
    setRefundAmount(remaining);
    setRefundOpen(true);
  };
  const submitRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (Number.isNaN(amount) || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setRefundBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/refund`, { amount });
      toast.success(`Refunded $${data.amount.toFixed(2)} — buyer emailed.`);
      setRefundOpen(false);
      loadOrder();
    } catch (e) {
      toast.error(formatApiError(e, "Refund failed"));
    } finally {
      setRefundBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!order) return;
    if (!window.confirm(`Delete order #${order.order_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success("Order deleted");
      nav("/admin?tab=orders");
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  const copyToClipboard = (val, msg = "Copied") => {
    if (!val) return;
    try {
      navigator.clipboard.writeText(String(val));
      toast.success(msg);
    } catch { /* no-op */ }
  };

  // ─── Derived values ────────────────────────────────────────────────────────
  const totalVials = useMemo(
    () => (order ? totalVialsForOrder(order, productsById) : null),
    [order, productsById],
  );

  const activity = useMemo(() => buildActivity(order), [order]);

  // ─── Render states ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-bone" data-testid="workspace-loading">
        <p className="overline">Loading order…</p>
      </div>
    );
  }
  if (err || !order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-bone px-6" data-testid="workspace-error">
        <div className="text-center">
          <p className="overline text-oxblood mb-2">Order unavailable</p>
          <p className="text-sm text-charcoal/75 mb-4">{err || "This order could not be loaded."}</p>
          <Link to="/admin?tab=orders" className="inline-flex items-center gap-2 px-4 py-2 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-xs uppercase rounded-full">
            <ArrowLeft size={12}/> Back to orders
          </Link>
        </div>
      </div>
    );
  }

  const addr = order.address || {};
  const pm = paymentPill(order);
  const refunded = Number(order.refunded_amount || 0);
  const canRefund = order.payment_method === "stripe" && refunded < Number(order.total || 0) - 0.01;
  const shipMethod = shippingMethod(order);
  const trackUrl = trackingUrl(order.tracking_carrier, order.tracking_number);

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-order-workspace">
      {/* A. STICKY HEADER (contextual, Phase 1 focus-mode language) */}
      <div className="sticky top-0 z-30 bg-bone/95 backdrop-blur border-b border-borderSubtle" data-testid="workspace-header">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-[13px] uppercase tracking-[0.22em] font-mono">
              <Link to="/admin?tab=orders" className="inline-flex items-center gap-1.5 text-charcoal/70 hover:text-charcoal" data-testid="workspace-back">
                <ArrowLeft size={12}/> Orders
              </Link>
              <span className="text-charcoal/30">|</span>
              <span className="text-charcoal">Order #{order.order_number}</span>
            </div>
            <div className="flex items-center gap-3 relative" ref={actionsRef}>
              <button
                onClick={() => setActionsOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-oxblood text-bone hover:bg-charcoal tracking-[0.2em] text-[12px] uppercase transition-colors rounded-full"
                data-testid="workspace-actions-trigger"
              >
                <MoreHorizontal size={14}/> Actions
              </button>
              {actionsOpen && (
                <div className="absolute right-0 top-full mt-2 z-40 w-64 rounded-md border border-borderSubtle bg-white shadow-xl py-1 text-left" data-testid="workspace-actions-menu">
                  <MenuItem icon={<Pencil size={13}/>} label="Edit order" onClick={() => { setEditing(true); setActionsOpen(false); }} testid="workspace-action-edit"/>
                  <MenuItem icon={<Mail size={13}/>} label={order.note_to_buyer ? "Edit buyer note" : "Add buyer note"} onClick={() => { openNote(); setActionsOpen(false); }} testid="workspace-action-note"/>
                  {!order.local_delivery && (
                    <MenuItem icon={<Truck size={13}/>} label={order.tracking_number ? "Update tracking" : "Add tracking"} onClick={() => { openTracking(); setActionsOpen(false); }} testid="workspace-action-tracking"/>
                  )}
                  <a
                    href={`${BACKEND}/api/orders/${order.id}/slip.pdf`}
                    target="_blank" rel="noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-charcoal hover:bg-bone transition-colors"
                    data-testid="workspace-action-slip"
                  >
                    <Printer size={13} className="text-charcoal/60"/> Print packing slip
                  </a>
                  {canRefund && (
                    <MenuItem icon={<RotateCcw size={13}/>} label="Refund (Stripe)" onClick={() => { openRefund(); setActionsOpen(false); }} testid="workspace-action-refund"/>
                  )}
                  <div className="border-t border-borderSubtle my-1"/>
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Change status</p>
                    <select
                      value={order.status}
                      onChange={(e) => { handleStatusChange(e.target.value); setActionsOpen(false); }}
                      className="w-full h-8 text-[12px] font-mono uppercase tracking-widest border border-borderSubtle rounded-sm bg-white px-2"
                      data-testid="workspace-status-select"
                    >
                      <option value="pending_payment">Pending payment</option>
                      <option value="paid">Paid</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="abandoned">Abandoned</option>
                    </select>
                  </div>
                  <div className="border-t border-borderSubtle my-1"/>
                  <button
                    onClick={() => { setActionsOpen(false); handleDelete(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-oxblood hover:bg-oxblood/[0.06] transition-colors"
                    data-testid="workspace-action-delete"
                  >
                    <Trash2 size={13}/> Delete order
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Identity strip */}
          <div className="mt-3 flex items-baseline gap-4 flex-wrap">
            <h1 className="font-serif text-3xl md:text-4xl leading-none text-charcoal">
              #{order.order_number}
            </h1>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border ${statusTone(order.status)}`} data-testid="workspace-status-pill">
              {statusLabel(order.status)}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border ${pm.tone}`} data-testid="workspace-payment-pill">
              {pm.label}
            </span>
            <span className="font-mono text-xl text-oxblood" data-testid="workspace-total">
              ${Number(order.total).toFixed(2)}
            </span>
            <span className="text-[12px] font-mono uppercase tracking-widest text-charcoal/60" data-testid="workspace-created">
              Placed {fmtDateTime(order.created_at)}
            </span>
            <span className="text-[12px] font-mono uppercase tracking-widest text-charcoal/60" data-testid="workspace-payment-method">
              via {order.payment_method}
            </span>
            {order.customer_deleted && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-charcoal/10 text-charcoal/75 border border-borderSubtle">
                Customer deleted
              </span>
            )}
          </div>
        </div>
      </div>

      {/* MAIN — single scroll */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8 space-y-10">
        {/* B. CUSTOMER + SHIPPING  ·  PAYMENT */}
        <section className="grid lg:grid-cols-[1.4fr_1fr] gap-6" data-testid="workspace-section-customer-payment">
          {/* Customer + Shipping */}
          <div className="border border-borderSubtle bg-white rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={14} className="text-charcoal/60"/>
              <h2 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-charcoal">
                Customer &amp; Shipping
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">Customer</p>
                <p className="font-medium text-[15px]" data-testid="workspace-customer-name">{order.customer_name || "—"}</p>
                <a
                  href={`mailto:${order.customer_email}`}
                  className="text-[13px] text-charcoal/75 hover:text-oxblood font-mono break-all block mt-0.5"
                  data-testid="workspace-customer-email"
                >
                  {order.customer_email}
                </a>
                {addr.phone && (
                  <p className="text-[13px] text-charcoal/75 font-mono mt-0.5" data-testid="workspace-customer-phone">{addr.phone}</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">
                  {order.local_delivery_mode === "pickup" ? "Pickup — customer address"
                    : order.local_delivery ? "Local drop-off"
                    : "Ship to"}
                </p>
                {(addr.line1 || addr.city) ? (
                  <div className="text-[14px] text-charcoal leading-relaxed" data-testid="workspace-ship-address">
                    {addr.name && <p>{addr.name}</p>}
                    {addr.line1 && <p>{addr.line1}</p>}
                    {addr.line2 && <p>{addr.line2}</p>}
                    {(addr.city || addr.state || addr.postal_code) && (
                      <p>
                        {[addr.city, addr.state].filter(Boolean).join(", ")}
                        {addr.postal_code ? ` ${addr.postal_code}` : ""}
                      </p>
                    )}
                    {addr.country && <p>{addr.country}</p>}
                  </div>
                ) : (
                  <p className="text-[13px] text-charcoal/60 italic">No address on file</p>
                )}
              </div>
            </div>
            {/* Shipping method row */}
            <div className="mt-5 pt-4 border-t border-borderSubtle grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">Shipping method</p>
                <p className="text-[14px] text-charcoal" data-testid="workspace-ship-method">{shipMethod}</p>
                {order.shipping_snapshot?.delivery_days != null && (
                  <p className="text-[12px] text-charcoal/60 mt-0.5">
                    Est. {order.shipping_snapshot.delivery_days} business day{order.shipping_snapshot.delivery_days === 1 ? "" : "s"}
                  </p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">Shipping cost</p>
                <p className="font-mono text-[14px] text-charcoal">${Number(order.shipping || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="border border-borderSubtle bg-white rounded-lg p-6" data-testid="workspace-payment-panel">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard size={14} className="text-charcoal/60"/>
              <h2 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-charcoal">Payment</h2>
            </div>
            <dl className="space-y-1.5 text-[14px] font-mono">
              <Row label="Method"><span className="uppercase">{order.payment_method}</span></Row>
              <Row label="State"><span>{pm.label}</span></Row>
              <div className="border-t border-borderSubtle my-2"/>
              <Row label="Subtotal">${Number(order.subtotal || 0).toFixed(2)}</Row>
              {Number(order.discount || 0) > 0 && (
                <Row label={`Discount${order.discount_code ? ` (${order.discount_code})` : ""}`}>
                  <span className="text-oxblood">−${Number(order.discount).toFixed(2)}</span>
                </Row>
              )}
              <Row label="Shipping">${Number(order.shipping || 0).toFixed(2)}</Row>
              <div className="flex justify-between border-t border-borderSubtle pt-2 mt-2 text-[16px] font-semibold text-oxblood">
                <span>Total</span>
                <span data-testid="workspace-payment-total">${Number(order.total || 0).toFixed(2)}</span>
              </div>
              {refunded > 0 && (
                <>
                  <div className="border-t border-borderSubtle my-2"/>
                  <Row label="Refunded"><span className="text-oxblood">−${refunded.toFixed(2)}</span></Row>
                  {order.last_refund_at && (
                    <p className="text-[11px] text-charcoal/60 mt-0.5 pl-0" data-testid="workspace-last-refund-at">
                      Last refund {fmtDateTime(order.last_refund_at)}
                    </p>
                  )}
                </>
              )}
            </dl>
          </div>
        </section>

        {/* C. ORDER ITEMS */}
        <section data-testid="workspace-section-items">
          <div className="flex items-center gap-2 mb-3">
            <Package size={14} className="text-charcoal/60"/>
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-charcoal">
              Order Items
            </h2>
            <span className="text-[11px] font-mono text-charcoal/60">
              · {(order.items || []).length} line{(order.items || []).length !== 1 ? "s" : ""}
              {totalVials != null && ` · ${totalVials} total vial${totalVials !== 1 ? "s" : ""}`}
            </span>
          </div>
          <div className="border border-borderSubtle bg-white rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bone border-b border-borderSubtle">
                <tr className="text-left">
                  <th className="overline px-4 py-3">Product</th>
                  <th className="overline px-4 py-3">Strength</th>
                  <th className="overline px-4 py-3">Format</th>
                  <th className="overline px-4 py-3 text-right">Qty</th>
                  <th className="overline px-4 py-3 text-right">Total vials</th>
                  <th className="overline px-4 py-3 text-right">Unit price</th>
                  <th className="overline px-4 py-3 text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map((it, idx) => {
                  const total = totalVialsForLine(it, productsById);
                  const per = vialsPerUnit(it, productsById);
                  const isComp = it.price_override != null && Number(it.price_override) === 0;
                  return (
                    <tr key={idx} className="border-t border-borderSubtle" data-testid={`workspace-item-${idx}`}>
                      <td className="px-4 py-3 align-top">
                        <p className="font-medium leading-tight">{it.name || "—"}</p>
                        {it.sku && <p className="text-[11px] font-mono uppercase tracking-widest text-charcoal/60 mt-0.5">{it.sku}</p>}
                        {isComp && <span className="mt-1 inline-block text-[10px] uppercase tracking-[0.18em] font-mono text-oxblood">Comp</span>}
                        {it.price_override != null && Number(it.price_override) > 0 && (
                          <span className="mt-1 inline-block text-[10px] uppercase tracking-[0.18em] font-mono text-oxblood">Custom price</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-charcoal/80">{strengthForItem(it, productsById)}</td>
                      <td className="px-4 py-3 text-charcoal/80">{formatLabelForItem(it, productsById)}</td>
                      <td className="px-4 py-3 font-mono text-right">{it.quantity}</td>
                      <td className="px-4 py-3 font-mono text-right text-charcoal/80">
                        {total == null ? "—" : total}
                        {per != null && per > 1 && (
                          <span className="block text-[10px] text-charcoal/50">({per}/kit)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-right">${Number(it.price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 font-mono text-right text-oxblood">
                        ${(Number(it.price || 0) * Number(it.quantity || 0)).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-bone border-t border-borderSubtle">
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-right text-[13px] text-charcoal/75">Subtotal</td>
                  <td className="px-4 py-3 text-right font-mono">${Number(order.subtotal || 0).toFixed(2)}</td>
                </tr>
                {Number(order.discount || 0) > 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-right text-[13px] text-charcoal/75">Discount{order.discount_code ? ` (${order.discount_code})` : ""}</td>
                    <td className="px-4 py-3 text-right font-mono text-oxblood">−${Number(order.discount).toFixed(2)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-right text-[13px] text-charcoal/75">Shipping</td>
                  <td className="px-4 py-3 text-right font-mono">${Number(order.shipping || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-right text-[15px] font-semibold">Total</td>
                  <td className="px-4 py-3 text-right font-mono text-[15px] font-bold text-oxblood">${Number(order.total || 0).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {/* D. FULFILLMENT + TRACKING */}
        <FulfillmentPanel
          order={order}
          onOpenTracking={openTracking}
          onRefresh={loadOrder}
          onStatusChange={handleStatusChange}
        />

        {/* E. NOTES & COMMUNICATION */}
        <section className="border border-borderSubtle bg-white rounded-lg p-6" data-testid="workspace-section-notes">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-charcoal/60"/>
              <h2 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-charcoal">Notes &amp; Communication</h2>
            </div>
            <button
              onClick={openNote}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono border border-borderSubtle rounded-full hover:border-oxblood hover:text-oxblood transition-colors"
              data-testid="workspace-edit-note"
            >
              {order.note_to_buyer ? "Edit buyer note" : "Add buyer note"}
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">Note to buyer (public)</p>
              {order.note_to_buyer ? (
                <p className="text-[14px] text-charcoal whitespace-pre-wrap leading-relaxed border-l-[3px] border-oxblood pl-3 py-1" data-testid="workspace-note-to-buyer">
                  {order.note_to_buyer}
                </p>
              ) : (
                <p className="text-[13px] text-charcoal/60 italic">None. Buyers see this on their slip and status emails.</p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">Admin notes (internal)</p>
              {order.admin_notes ? (
                <p className="text-[14px] text-charcoal whitespace-pre-wrap leading-relaxed border-l-[3px] border-charcoal/40 pl-3 py-1" data-testid="workspace-admin-notes">
                  {order.admin_notes}
                </p>
              ) : (
                <p className="text-[13px] text-charcoal/60 italic">
                  None. Edit through the Edit Order dialog to set admin-only notes.
                </p>
              )}
            </div>
          </div>
          {/* iter185 UI polish — Email History placeholder; honest, no dev copy. */}
          <div className="mt-5 pt-5 border-t border-borderSubtle">
            <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1.5">Email history</p>
            <p className="text-[13px] text-charcoal/60 italic" data-testid="workspace-email-history">Not available</p>
          </div>
        </section>

        {/* F. ACTIVITY — Phase 3A · event-log driven */}
        <section data-testid="workspace-section-activity">
          <div className="flex items-center gap-2 mb-3">
            <History size={14} className="text-charcoal/60"/>
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-charcoal">Activity</h2>
          </div>
          <OrderActivityTimeline
            orderId={orderId}
            orderStatus={order?.status}
            onMarkDelivered={() => handleStatusChange("delivered")}
          />
        </section>

        {/* Bottom back link — quick return */}
        <div className="pt-4 pb-8">
          <Link
            to="/admin?tab=orders"
            className="inline-flex items-center gap-2 px-4 py-2 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-xs uppercase rounded-full"
            data-testid="workspace-back-bottom"
          >
            <ArrowLeft size={12}/> Back to orders
          </Link>
        </div>
      </div>

      {/* Reused Edit dialog — same component the Orders table uses. */}
      <EditOrderDialog
        open={editing}
        onOpenChange={(o) => {
          if (!o) { editDraftRef.current = null; setEditing(false); }
        }}
        order={order}
        onSaved={() => { setEditing(false); loadOrder(); }}
        draftRef={editDraftRef}
      />

      {/* Note mini-dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="rounded-2xl max-w-lg bg-bone border-borderSubtle" data-testid="workspace-note-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Note to buyer</DialogTitle></DialogHeader>
          <p className="text-[13px] text-charcoal/75 -mt-2 mb-1">
            Shown on the buyer's slip (screen + PDF) and inside status-change emails.
          </p>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            maxLength={2000}
            rows={5}
            placeholder="Thanks for your order — vials packed with ice."
            className="w-full text-sm bg-white border border-borderSubtle rounded p-3 focus:outline-none focus:border-charcoal"
            data-testid="workspace-note-input"
            autoFocus
          />
          <p className="text-[13px] text-charcoal/75 text-right">{noteDraft.length}/2000</p>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setNoteOpen(false)} className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal">Cancel</button>
            <button onClick={saveNote} className="py-2.5 px-6 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors" data-testid="workspace-note-save">
              Save note
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking mini-dialog */}
      <Dialog open={trackingOpen} onOpenChange={setTrackingOpen}>
        <DialogContent className="rounded-2xl max-w-md bg-bone border-borderSubtle" data-testid="workspace-tracking-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Tracking</DialogTitle></DialogHeader>
          <p className="text-[13px] text-charcoal/75 -mt-2 mb-2">
            When a new tracking number is saved, we auto-email the buyer with a carrier link.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="overline font-bold text-charcoal">Carrier</Label>
              <select
                value={trackingDraft.carrier}
                onChange={(e) => setTrackingDraft({ ...trackingDraft, carrier: e.target.value })}
                className="mt-2 w-full h-10 rounded-none border border-borderSubtle bg-white px-3 text-sm focus:outline-none focus:border-charcoal"
                data-testid="workspace-tracking-carrier"
              >
                <option value="">— None —</option>
                <option value="USPS">USPS</option>
                <option value="UPS">UPS</option>
                <option value="FedEx">FedEx</option>
                <option value="DHL">DHL</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label className="overline font-bold text-charcoal">Tracking number</Label>
              <Input
                value={trackingDraft.number}
                onChange={(e) => setTrackingDraft({ ...trackingDraft, number: e.target.value })}
                className="mt-2 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood"
                maxLength={80}
                data-testid="workspace-tracking-number"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <button onClick={() => setTrackingOpen(false)} className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal">Cancel</button>
            <button onClick={saveTracking} className="py-2.5 px-6 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors" data-testid="workspace-tracking-save">
              Save tracking
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund mini-dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="rounded-2xl max-w-md bg-bone border-borderSubtle" data-testid="workspace-refund-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Refund</DialogTitle></DialogHeader>
          {(() => {
            const alreadyRefunded = Number(order.refunded_amount || 0);
            const remaining = Number(order.total) - alreadyRefunded;
            return (
              <>
                <p className="text-[13px] text-charcoal/75 -mt-2 mb-3">
                  Order <span className="font-mono text-charcoal">#{order.order_number}</span> · Total <span className="font-mono text-charcoal">${Number(order.total).toFixed(2)}</span>
                  {alreadyRefunded > 0 && <> · Already refunded <span className="font-mono text-oxblood">${alreadyRefunded.toFixed(2)}</span></>}
                </p>
                <div>
                  <Label className="overline font-bold text-charcoal">Amount to refund</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75 font-mono text-sm">$</span>
                    <Input
                      type="number" step="0.01" min="0.01" max={remaining.toFixed(2)}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="pl-7 rounded-none border-borderSubtle bg-white focus-visible:ring-0 focus-visible:border-oxblood font-mono"
                      data-testid="workspace-refund-amount"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button type="button" onClick={() => setRefundAmount(remaining.toFixed(2))} className="text-[12px] uppercase tracking-[0.18em] px-2.5 py-1 border border-borderSubtle hover:border-oxblood hover:text-oxblood rounded-full">Full · ${remaining.toFixed(2)}</button>
                    <button type="button" onClick={() => setRefundAmount((remaining / 2).toFixed(2))} className="text-[12px] uppercase tracking-[0.18em] px-2.5 py-1 border border-borderSubtle hover:border-oxblood hover:text-oxblood rounded-full">Half</button>
                  </div>
                  <p className="text-[12px] text-charcoal/75 mt-2">Max refundable: <span className="font-mono">${remaining.toFixed(2)}</span></p>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button onClick={() => setRefundOpen(false)} className="py-2.5 px-4 text-[13px] uppercase tracking-[0.22em] text-charcoal/75 hover:text-charcoal">Cancel</button>
                  <button onClick={submitRefund} disabled={refundBusy} className="py-2.5 px-6 bg-oxblood hover:bg-charcoal text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50" data-testid="workspace-refund-submit">
                    {refundBusy ? "Refunding…" : `Refund $${parseFloat(refundAmount || 0).toFixed(2)}`}
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Fulfillment sub-panel (reuses existing endpoints; no new logic) ────────
function FulfillmentPanel({ order, onOpenTracking, onRefresh, onStatusChange }) {
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState(order.fulfillment_rates || null);
  const trackUrl = order.tracking_url || trackingUrl(order.tracking_carrier, order.tracking_number);

  const buyBuyerLabel = async () => {
    if (!window.confirm("Buy shipping label using the rate the buyer picked at checkout?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/fulfillment/orders/${order.id}/buy-buyer-label`);
      toast.success(`Label purchased · ${data.tracking_number || "tracking assigned"}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e, "Buy-label failed"));
    } finally {
      setBusy(false);
    }
  };

  const refreshRates = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/fulfillment/orders/${order.id}/refresh-rates`);
      setRates(data.options || []);
      toast.success(`Fetched ${(data.options || []).length} live rate${(data.options || []).length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e, "Rate refresh failed"));
    } finally {
      setBusy(false);
    }
  };

  const buyPickedLabel = async (option_id) => {
    if (!window.confirm("Buy this rate as a shipping label?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/fulfillment/orders/${order.id}/buy-label`, { option_id });
      toast.success(`Label purchased · ${data.tracking_number || "tracking assigned"}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e, "Buy-label failed"));
    } finally {
      setBusy(false);
    }
  };

  const voidLabel = async () => {
    if (!window.confirm("Void the shipping label? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/fulfillment/orders/${order.id}/void-label`);
      toast.success("Label voided");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e, "Void failed"));
    } finally {
      setBusy(false);
    }
  };

  const buyerRate = order.shipping_snapshot;
  const hasLabel = !!order.shippo_transaction_id;
  const isLocal = !!order.local_delivery;
  const isCancelled = order.status === "cancelled";
  const canBuyBuyerLabel = !hasLabel && !isLocal && !isCancelled && buyerRate?.shippo_rate_id;

  return (
    <section className="border border-borderSubtle bg-white rounded-lg p-6" data-testid="workspace-section-fulfillment">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Truck size={14} className="text-charcoal/60"/>
          <h2 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-charcoal">Fulfillment &amp; Tracking</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isCancelled && !isLocal && (
            <button
              onClick={onOpenTracking}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono border border-borderSubtle rounded-full hover:border-oxblood hover:text-oxblood transition-colors"
              data-testid="workspace-fulfillment-edit-tracking"
            >
              <Truck size={11}/> {order.tracking_number ? "Update tracking" : "Add tracking"}
            </button>
          )}
          {(order.status === "paid" && !hasLabel && !isLocal) && (order.status !== "shipped") && (
            <button
              onClick={() => onStatusChange("shipped")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono border border-borderSubtle rounded-full hover:border-oxblood hover:text-oxblood transition-colors"
              data-testid="workspace-fulfillment-mark-shipped"
            >
              Mark shipped
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left: current state */}
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Fulfillment state</p>
            <p className="text-[14px] text-charcoal" data-testid="workspace-fulfillment-state">
              {isLocal
                ? (order.local_delivery_mode === "pickup" ? "Local pickup" : "Local delivery")
                : hasLabel
                  ? "Shipping label purchased"
                  : order.status === "paid"
                    ? "Ready to ship — no label yet"
                    : "Awaiting payment"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Carrier</p>
              <p className="text-[14px] text-charcoal">{order.tracking_carrier || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Service</p>
              <p className="text-[14px] text-charcoal">{order.shipping_snapshot?.service_display || order.shipping_snapshot?.service || "—"}</p>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Tracking number</p>
            {order.tracking_number ? (
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[14px] font-mono text-charcoal break-all" data-testid="workspace-tracking-num">{order.tracking_number}</p>
                {trackUrl && (
                  <a href={trackUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-mono text-oxblood hover:text-charcoal" data-testid="workspace-tracking-link">
                    Track <ExternalLink size={10}/>
                  </a>
                )}
              </div>
            ) : (
              <p className="text-[13px] text-charcoal/60 italic">Not yet set</p>
            )}
          </div>
        </div>

        {/* Right: label state */}
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Shipping label</p>
            {hasLabel ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border bg-mossBg text-moss border-mossBorder">Purchased</span>
                {order.label_url && (
                  <a href={order.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-mono text-oxblood hover:text-charcoal" data-testid="workspace-label-url">
                    Open label PDF <ExternalLink size={10}/>
                  </a>
                )}
              </div>
            ) : (
              <p className="text-[13px] text-charcoal/60 italic">No label purchased</p>
            )}
          </div>
          {(order.label_purchased_at || order.label_purchased_by) && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Label purchased</p>
              <p className="text-[13px] text-charcoal" data-testid="workspace-label-meta">
                {order.label_purchased_at ? fmtDateTime(order.label_purchased_at) : "—"}
                {order.label_purchased_by && <span className="text-charcoal/60"> · by {order.label_purchased_by}</span>}
              </p>
            </div>
          )}
          {/* Action row for label lifecycle — only when relevant (not local, not cancelled) */}
          {!isLocal && !isCancelled && (
            <div className="flex flex-wrap gap-2 pt-1">
              {canBuyBuyerLabel && (
                <button
                  onClick={buyBuyerLabel}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono bg-oxblood hover:bg-charcoal text-bone rounded-full transition-colors disabled:opacity-50"
                  data-testid="workspace-buy-buyer-label"
                  title={`Buy the ${buyerRate.service_display || buyerRate.carrier || "buyer-picked"} rate`}
                >
                  Buy buyer label {buyerRate.rate_cents != null && `· $${(buyerRate.rate_cents / 100).toFixed(2)}`}
                </button>
              )}
              {!hasLabel && (
                <button
                  onClick={refreshRates}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono border border-borderSubtle rounded-full hover:border-oxblood hover:text-oxblood transition-colors disabled:opacity-50"
                  data-testid="workspace-refresh-rates"
                >
                  <RefreshCw size={10}/> Refresh rates
                </button>
              )}
              {hasLabel && (
                <button
                  onClick={voidLabel}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono border border-oxblood text-oxblood rounded-full hover:bg-oxblood hover:text-bone transition-colors disabled:opacity-50"
                  data-testid="workspace-void-label"
                >
                  <XCircle size={10}/> Void label
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rate picker when admin refreshed rates */}
      {Array.isArray(rates) && rates.length > 0 && !hasLabel && (
        <div className="mt-6 border-t border-borderSubtle pt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-2">Available live rates</p>
          <div className="space-y-1.5" data-testid="workspace-live-rates">
            {rates.map((r) => (
              <div key={r.option_id} className="flex items-center justify-between gap-3 border border-borderSubtle rounded p-2.5">
                <div className="text-[13px]">
                  <span className="font-medium">{r.service_display || r.service || r.carrier}</span>
                  {r.delivery_days != null && <span className="text-charcoal/60"> · {r.delivery_days}d</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[13px] text-oxblood">${((r.rate_cents || 0) / 100).toFixed(2)}</span>
                  <button
                    onClick={() => buyPickedLabel(r.option_id)}
                    disabled={busy}
                    className="text-[11px] uppercase tracking-[0.18em] font-mono px-2.5 py-1 border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone rounded-full transition-colors disabled:opacity-50"
                  >
                    Buy label
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLocal && (
        <p className="mt-4 text-[12px] text-charcoal/60 italic">
          Local {order.local_delivery_mode === "pickup" ? "pickup" : "delivery"} — no shipping label required.
        </p>
      )}
    </section>
  );
}

// ─── Activity builder — pure function over stored data. No fabrication. ────
function buildActivity(order) {
  if (!order) return [];
  const events = [];
  const history = Array.isArray(order.status_history) ? order.status_history : [];
  const editHist = Array.isArray(order.edit_history) ? order.edit_history : [];

  const statusLabelForEvent = (s) => ({
    pending_payment: "Order placed",
    paid: "Payment received",
    shipped: "Order marked shipped",
    delivered: "Order marked delivered",
    cancelled: "Order cancelled",
    abandoned: "Checkout abandoned",
  }[s] || `Status → ${s}`);

  for (const h of history) {
    if (!h?.at) continue;
    events.push({
      at: h.at,
      by: h.by || null,
      label: statusLabelForEvent(h.status),
      detail: null,
    });
  }
  for (const e of editHist) {
    if (!e?.at) continue;
    const { labels, groupKeys } = describeEdit(e.fields_changed);
    // iter185 UI polish — collapse duplicate rows. An edit whose only
    // meaningful group is a status change is already covered by
    // status_history — don't render it twice.
    if (labels.length === 0) continue;
    if (groupKeys.length === 1 && groupKeys[0] === "status") continue;
    // Also drop the "Order status" chip when the edit changed other fields
    // too — the status_history row will surface that transition explicitly.
    const filtered = labels.filter((_, i) => groupKeys[i] !== "status");
    // (For unknown/unmatched labels we kept them in `labels` but they never
    // appear in groupKeys — so filtered.length can be larger than groupKeys.)
    const shown = filtered.length > 0 ? filtered : labels;
    const summary = joinPhrases(shown);
    events.push({
      at: e.at,
      by: e.by || null,
      // Title reads like "Order updated" for multi-group edits, or the group
      // itself when only one applies (e.g. "Tracking updated").
      label: shown.length === 1
        ? `${shown[0]} updated`
        : "Order updated",
      detail: shown.length > 1 ? `${summary} updated` : null,
    });
  }
  if (order.label_purchased_at) {
    events.push({
      at: order.label_purchased_at,
      by: order.label_purchased_by || null,
      label: "Shipping label purchased",
      detail: order.tracking_number ? `Tracking ${order.tracking_number}` : null,
    });
  }
  if (order.last_refund_at) {
    events.push({
      at: order.last_refund_at,
      by: null,
      label: "Refund issued",
      detail: `Refunded to date: $${Number(order.refunded_amount || 0).toFixed(2)}`,
    });
  }
  events.sort((a, b) => new Date(b.at) - new Date(a.at));
  return events;
}

// ─── Presentational primitives ─────────────────────────────────────────────
function Row({ label, children }) {
  return (
    <div className="flex justify-between items-baseline">
      <dt className="text-charcoal/60 text-[13px] uppercase tracking-[0.14em] font-mono">{label}</dt>
      <dd className="text-charcoal font-mono text-[14px]">{children}</dd>
    </div>
  );
}

function MenuItem({ icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left text-charcoal hover:bg-bone transition-colors"
      data-testid={testid}
    >
      <span className="text-charcoal/60">{icon}</span>{label}
    </button>
  );
}
